<template>
  <i-modal
    :title="modalTitle"
    :value="value"
    :load="load"
    @input="$emit('input', $event)"
    :max-width="modalMaxWidth"
    dontSave
  >
    <v-card-text>
      <v-row class="my-0">
        <div class="col-6">
          <h2 class="title-responsive text-center">
            {{ assignableUser }} {{ mode == 'driver' ? $t('at_home') : '' }}
          </h2>
        </div>
        <div class="col-6">
          <h2 class="title-responsive text-center">{{ assignedTitle }}</h2>
        </div>
      </v-row>
      <v-row class="my-0">
        <div class="col-6">
          <draggable class="dialAssignItem-group" group="people" :list="items">
            <div
              v-for="item in items"
              :key="item.id"
              class="dialAssignItem-item elevation-1"
            >
              <i-image
                :preview="true"
                :readonly="true"
                :style="{
                  'border-radius': '50%',
                  width: '60px !important',
                  height: '60px !important'
                }"
                :value="itemImage(item)"
              />
              <div class="pl-1">
                <!--
                  Sección delivery, subOrdenes a seleccionar para ser entregadas
                  por cada conductor
                -->
                <suborders-info v-if="isDriverMode" :item="item" />

                <!--
                  Sección para modo producción (departamentos)
                -->
                <div v-else-if="isProductionMode">
                  {{ item.name }}
                </div>

                <!--
                  Sección subOrden, usuarios disponibles para asignar a una
                  subOrden
                -->
                <p v-else class="font-weight-bold mb-0">
                  {{ `${item.first_name} ${item.last_name}` }}
                </p>
                <v-chip small color="secondary">
                  {{ assignableUser }}
                </v-chip>
              </div>
            </div>
          </draggable>
          <pagination
            :items="items"
            :perPage.sync="perPage"
            :total="this.total"
            :page="this.page"
            @changePage="changePage"
          />
        </div>

        <div class="col-6">
          <draggable
            class="dialAssignItem-group"
            group="people"
            :list="assignedItems"
            @change="onUnpublishedChange"
          >
            <div v-for="item in assignedItems" :key="item.id">
              <div
                v-if="itemHasData(item)"
                class="dialAssignItem-item elevation-1"
              >
                <!-- Ítems asignados -->
                <i-image
                  v-if="!isProductionMode"
                  :preview="true"
                  :readonly="true"
                  :style="{
                    'border-radius': '50%',
                    width: '60px !important',
                    height: '60px !important'
                  }"
                  :value="assignedImage(item)"
                />
                <div class="pl-1">
                  <!-- Sección delivery, subOrdenes a entregar por conductor -->
                  <suborders-assigned-info
                    v-if="isDriverMode && item.suborder"
                    :item="item.suborder"
                    :client="item.client"
                  />
                  <!--
                    Sección para modo producción (departamentos)
                  -->
                  <div v-else-if="isProductionMode">
                    {{ item.department_object.name }}
                  </div>
                  <!-- Sección subOrden, usuarios asignados a una subOrden -->
                  <p v-else class="font-weight-bold mb-0">
                    {{ item.user_data.name }}
                  </p>
                  <status-chip
                    :status="item.status || ''"
                    :date="formatCompanyDate(item.assignament_date)"
                  />
                </div>
              </div>
            </div>
          </draggable>
        </div>
      </v-row>
    </v-card-text>
  </i-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import draggable from 'vuedraggable'
import subordersInfo from './subordersInfo.vue'
import subordersAssignedInfo from './subordersAssignedInfo.vue'
import pagination from './pagination.vue'
import statusChip from './statusChip.vue'

export default {
  components: {
    draggable,
    subordersInfo,
    subordersAssignedInfo,
    pagination,
    statusChip
  },
  data() {
    return {
      load: false,
      loading: false,
      items: [],
      assignedItems: [],
      total: 0,
      perPage: 8,
      page: 1
    }
  },
  props: {
    selectedItem: {
      required: true,
      type: [String, Number]
    },
    mode: {
      default: 'designer',
      type: String
    },
    value: {
      type: Boolean,
      default: false
    },
    template: {
      type: Number
    }
  },
  computed: {
    ...mapGetters({
      company: 'company/getCompanyData'
    }),
    /**
     * Computado que determina el nombre de la modal de los usuarios asignados
     *
     * @returns {string} - Título.
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    modalTitle() {
      const titlesMap = {
        designer: 'assignDesigner',
        production: 'assignProducer',
        delivery: 'assignDriver'
      }
      const key = titlesMap[this.mode] || 'assignSubOrder'
      return this.$t(key)
    },
    modalMaxWidth() {
      return this.mode === 'driver' ? '1800px' : '1200px'
    },
    /**
     * Computado que determina el nombre de la sección de los usuarios asignados
     *
     * @returns {string} - Título.
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    assignedTitle() {
      const titlesMap = {
        designer: 'assignaments.designers',
        production: 'assignaments.department',
        delivery: 'assignaments.drivers'
      }
      const key = titlesMap[this.mode] || 'assignaments.suborderToDrivers'
      return this.$t(key)
    },
    /**
     * Computado que determina el nombre de la sección de los usuarios
     * asignables y el distintivo de los usuarios.
     *
     * @returns {string} - Título.
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    assignableUser() {
      const titlesMap = {
        designer: 'designers',
        production: 'department',
        delivery: 'driver'
      }
      const key = titlesMap[this.mode] || 'suborder'
      return this.$tc(key, 2)
    },
    isDriverMode() {
      return this.mode === 'driver'
    },
    isProductionMode() {
      return this.mode === 'production'
    }
  },
  methods: {
    ...mapActions({
      showConfirmation: 'confirmation/confirmationValue'
    }),
    itemImage(item) {
      return this.isDriverMode ? item.thumbnail : item.photo
    },
    assignedImage(item) {
      return this.isDriverMode ? item.suborder.photo : item.user_data.photo
    },
    itemHasData(item) {
      return item.suborder || item.user_data || item.department
    },
    /**
     * Caso 1: Método que consulta las subOrdenes que están en estado
     * de orden 'delivery' con el campo delivery activo, que son las
     * que están listas para realizar la entrega
     * Caso 2: Método que consulta los usuarios que están disponibles
     * para ser asignados en la subOrden
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getItems() {
      // Caso 1
      if (this.mode == 'driver') {
        this.itemsAvalaible = await this.$api.sale.suborder.list({
          opt: {
            params: {
              status: 'at_home',
              company_id: this.company.pk
            }
          }
        })
      } else {
        // Caso 2
        let items = []
        const params = new URLSearchParams()
        switch (this.mode) {
          case 'designer':
            params.append('role', 'Designer')
            items = await this.$api.sale.availableusers({ opt: { params } })
            break
          case 'production':
            items = await this.$api.department.list({
              opt: {
                params: {
                  company_id: this.company.pk
                }
              }
            })
            break
          case 'delivery':
            params.append('role', 'Delivery')
            items = await this.$api.sale.availableusers({ opt: { params } })
            break
          default: // No action needed; items remains an empty array
        }
        this.itemsAvalaible = items
      }
      let deleteList = []
      if (this.mode !== 'designer') {
        this.itemsAvalaible = this.itemsAvalaible.data.results
        this.total = 1
      } else {
        this.total = Math.ceil(this.itemsAvalaible.data.count / this.perPage)
        this.itemsAvalaible = this.itemsAvalaible.data.results
      }
      this.$emit('avalaible', this.itemsAvalaible.length)
      if (this.itemsAvalaible.length > 0) {
        if (this.mode == 'driver') {
          // Eliminación de ítems asignados del caso 1
          this.itemsAvalaible.forEach((element) => {
            const forDelete = this.assignedItems?.findIndex(
              (item) => item.orderProduct === element.pk
            )
            if (forDelete !== -1) {
              deleteList.push(element.pk)
            }
          })
        } else {
          if (this.assignedItems && this.mode != 'production') {
            // Eliminación de ítems asignados del caso 2
            this.itemsAvalaible.forEach((element) => {
              const forDelete = element[
                this.mode === 'designer'
                  ? 'designs'
                  : this.mode === 'production'
                  ? 'deparments'
                  : this.mode === 'delivery'
                  ? 'drivers'
                  : ''
              ].findIndex(
                (item) =>
                  this.assignedItems.findIndex(
                    (assign) => assign.pk === item.pk
                  ) !== -1
              )
              if (forDelete !== -1) {
                deleteList.push(element.pk)
              }
            })
          } else {
            if (this.assignedItems?.[0]) {
              this.itemsAvalaible = this.itemsAvalaible.filter((item) => {
                const hasDepartment = Object.keys(
                  this.assignedItems?.[0]
                ).includes('department')
                const returnCondition = hasDepartment
                  ? item.pk !== this.assignedItems?.[0]?.department
                  : item.pk !== this.assignedItems?.[0]?.pk
                return returnCondition
              })
            }
          }
        }
        this.items = this.itemsAvalaible.filter(
          (item) => deleteList.indexOf(item.pk) === -1
        )
      }
    },
    /**
     * Formatea la fecha según el formato configurado para la empresa.
     *
     * @author Ing. Rodrigo Boet (rudmanmrrod at gmail)
     * @author Ing. Argenis Osorio (argenisosorio10 at gmail)
     */
    formatCompanyDate(date) {
      // Verifica si está configurado el formato de fecha en la empresa.
      if (this.company.format_date) {
        // Convierte el formato de la empresa a mayúsculas.
        let companyFormatDate = this.company.format_date.toUpperCase()

        // Si el formato de la empresa no incluye la hora, la concatenamos.
        if (!companyFormatDate.includes('HH:mm:ss')) {
          companyFormatDate += ' HH:mm:ss' // Agregamos la hora al formato.
        }

        // Formatea la fecha con el formato de la empresa (incluyendo la hora).
        return this.$moment(date).format(companyFormatDate)
      } else {
        // Formato de fecha y hora por defecto.
        return this.$moment(date).format('MM/DD/YYYY HH:mm:ss')
      }
    },
    async open() {
      this.load = true
      try {
        await this.getItemAssigned()
        await this.getItems()
      } catch (err) {
        this.dialog = false
      }
    },
    assign(id, notifyDesigner = false) {
      let item = {
        orderProduct: this.mode == 'driver' ? id : this.selectedItem,
        user: this.mode == 'driver' ? this.selectedItem : id,
        status: 'in_wait',
        changeDesigner: false,
        mode: this.mode == 'delivery' ? 'driver' : this.mode,
        delivery_date: null, // se recibe desde la app
        assignament_date: new Date().toISOString(),
        project_leader:
          this.mode !== 'deliver'
            ? this.assignedItems.findIndex(
                (element) => element.project_leader === true
              ) === -1
            : false
      }
      if (item.mode == 'designer' && item.project_leader) {
        var audio = new Audio(require('@/../public/sounds/sound.mp3'))
        audio.play()
      }
      if (this.mode == 'delivery' && this.assignedItems.length > 1) {
        this.$toast.error(`${this.$t('assignedOneDriver')}`)
      } else {
        // Asignar usuarios tipo, diseñador, productor o driver
        // a una subOrden
        if (this.mode != 'production') {
          this.$api.suborder.assigneduser
            .create({
              form: item,
              opt: {
                params: {
                  template: this.template
                }
              }
            })
            .then((response) => {
              this.getItemAssigned()
              this.getItems()
              this.$emit('updateAssignItems')
              this.$emit('avalaible')
              this.columnsDisabled = false
              if (this.mode === 'designer' && notifyDesigner) {
                const subName = response.data.suborder?.name || ''
                const subNumber = response.data.suborder?.number || ''
                const suborderRef = `${subName} ${subNumber}`.trim()
                const dialogData = {
                  active: true,
                  subject: this.$t('assigned_designer_subject', {
                    suborder: suborderRef
                  }),
                  body: this.$t('assigned_designer_body', {
                    designerName: response.data.user_data?.name || '',
                    companyName: this.company.name
                  }),
                  recipient_list: response.data.user_data?.email || '',
                  isStatusChange: false
                }
                this.$store.dispatch(
                  'utils/openDialogEmailNotification',
                  dialogData
                )
              }
            })
        } else {
          this.$api.sale.assignedDepartment
            .create({
              form: {
                orderProduct: this.selectedItem,
                department: id
              }
            })
            .then((response) => {
              this.getItemAssigned()
              this.getItems()
              this.$emit('updateAssignItems')
              this.$emit('avalaible')
              this.columnsDisabled = false
            })
        }
      }
    },
    remove(pk) {
      if (this.mode != 'production') {
        this.$api.suborder.assigneduser.remove({ pk }).then((response) => {
          this.getItemAssigned()
          this.getItems()
          this.$emit('updateAssignItems')
          this.$emit('avalaible')
          this.columnsDisabled = false
        })
      } else {
        this.$api.sale.assignedDepartment
          .remove({ pk })
          .then((response) => {
            if (response.status === 200 || response.status === 204) {
              this.getItemAssigned()
              this.getItems()
              this.$emit('updateAssignItems')
              this.$emit('avalaible')
              this.columnsDisabled = false
            }
          })
          .catch((error) => {
            console.error(error)
          })
      }
    },
    /**
     * Método que consulta los usuarios asignados a la subOrden
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getItemAssigned() {
      if (this.mode !== 'production') {
        let assigned = await this.$api.suborder.assigneduser.show({
          pk: this.selectedItem,
          opt: {
            params: {
              mode: this.mode
            }
          }
        })
        this.assignedItems = assigned.data
      } else {
        const query = await this.$api.sale.assignedDepartment.list({
          opt: {
            params: {
              orderProduct: this.selectedItem
            }
          }
        })
        this.assignedItems = query.data.results || []
      }
    },
    /**
     * Método para manejar el comportamiento al mover una suborden entre las
     * listas de asignables y asignadas
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    async onUnpublishedChange({ added, removed }) {
      if (this.assignedItems.length > 1 && this.mode === 'designer') {
        this.getItems()
        this.getItemAssigned()
        this.$toast.info(this.$tc('assignedOneDesigner'))
      } else {
        this.columnsDisabled = true
        if (added) await this.checkNotifyUser(added.element.pk)
        if (removed) await this.remove(removed.element.pk)
      }
    },
    async changePage() {
      this.getItems()
    },
    /**
     * Método que se encarga de verificar si el usuario tiene configurada la
     * notificación por correo.
     *
     * - Caso afirmativo: Se desplegará una modal en el proceso que se ejecuta
     * en el método assign
     * - Caso negativo (default): Solo se crea el registro nuevo.
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    async checkNotifyUser(userID) {
      let notifyEnabled = false
      try {
        const query = await this.$api.notifications.setting.byUser({
          opt: { params: { user__id: userID } }
        })
        if (query.data.results.length > 0) {
          notifyEnabled = query.data.results[0].email
        }
      } catch (error) {
        console.error(error)
      } finally {
        await this.assign(userID, notifyEnabled)
      }
    }
  },
  mounted() {
    this.getItemAssigned()
    this.getItems()
  },
  watch: {
    perPage() {
      this.getItems()
    }
  }
}
</script>

<style lang="sass">
.dialAssignItem-group
  min-height: 400px
  background-color: rgb(220 220 220 / .3)
  border-radius: 15px
  box-shadow: inset 0px 0px 2px 1px rgb(100 100 100 / .20)
  padding: 2px

.theme--dark
  .dialAssignItem-group
    background-color: rgb(0 0 0 / .25)
    box-shadow: inset 0px 0px 2px 1px rgb(255 255 255 / .20)

.dialAssignItem-item
  padding: 15px 10px
  display: flex
  flex-direction: row
  background-color: var(--v-auxbg-base)
  border-radius: 15px
  margin-bottom: 3px
  gap: 5px
  align-items: center
  &:hover
    cursor: grab
</style>
